import React, { useState, useEffect, useRef } from 'react';
import './SaveProgressModal.css';
import { FaUsers, FaWifi, FaSync } from 'react-icons/fa';
import { useSocket } from '../contexts/SocketContext';
import { API_BASE_URL } from '../utils/apiConfig';
import { safeSaveFetch, createUserFriendlyError } from '../utils/httpUtils';

interface SaveProgressModalProps {
  isVisible: boolean;
  onComplete: (results: any) => void;
  onError: (error: string) => void;
  studentsData: any[];
}

interface BatchProgress {
  currentBatch: number;
  totalBatches: number;
  processedStudents: number;
  totalStudents: number;
  percentage: number;
  estimatedTimeRemaining: number;
  currentStatus: string;
  coordinationEnabled: boolean;
  conflictsDetected: number;
  conflictsResolved: number;
}

const SaveProgressModal: React.FC<SaveProgressModalProps> = ({
  isVisible,
  onComplete,
  onError,
  studentsData
}) => {
  // 🔗 Usar el contexto HTTP para coordinación
  const { isConnected, isAuthenticated, notifications, clearNotification, addNotification } = useSocket();
  
  const [progress, setProgress] = useState<BatchProgress>({
    currentBatch: 0,
    totalBatches: 0,
    processedStudents: 0,
    totalStudents: studentsData.length,
    percentage: 0,
    estimatedTimeRemaining: 0,
    currentStatus: 'Preparando...',
    coordinationEnabled: isConnected && isAuthenticated,
    conflictsDetected: 0,
    conflictsResolved: 0
  });
  
  const [startTime, setStartTime] = useState<number>(0);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  // 🚨 FIX: Prevenir múltiples ejecuciones del guardado
  const isSavingRef = useRef(false);
  const lastSaveTimeRef = useRef(0);

  useEffect(() => {
    if (isVisible && studentsData.length > 0 && !isSavingRef.current) {
      // 🛡️ Protección: Solo ejecutar si no se ha guardado en los últimos 2 segundos
      const now = Date.now();
      if (now - lastSaveTimeRef.current > 2000) {
        console.log('💾 Starting save operation...');
        lastSaveTimeRef.current = now;
        isSavingRef.current = true;
        startSaving();
      } else {
        console.warn('⚠️  Save operation blocked - too soon after last save');
      }
    }
  }, [isVisible, studentsData]);

  // 🚨 FIX: Resetear bandera cuando el modal se oculta
  useEffect(() => {
    if (!isVisible) {
      isSavingRef.current = false;
    }
  }, [isVisible]);

  const startSaving = async () => {
    setStartTime(Date.now());
    setIsComplete(false);
    setHasError(false);
    
    const BATCH_SIZE = 5; // Procesar 5 estudiantes por lote para mejor estabilidad
    const totalBatches = Math.ceil(studentsData.length / BATCH_SIZE);
    
    // 🔗 Actualizar estado inicial con información de coordinación HTTP
    setProgress(prev => ({
      ...prev,
      totalBatches,
      coordinationEnabled: true, // Siempre habilitada con HTTP
      currentStatus: 'Iniciando guardado por lotes...',
      percentage: 0
    }));

    // 📡 Mostrar notificación sobre coordinación HTTP
    addNotification({
      type: 'success',
      title: 'Guardado por Lotes',
      message: 'Procesando datos en lotes para mostrar progreso en tiempo real'
    });

    try {
      let totalProcessed = 0;
      let batchTimes: number[] = [];
      const startTimeMs = Date.now();
      
      // 🚀 Procesar cada lote secuencialmente para mostrar progreso real
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const batchStartTime = Date.now();
        
        // 📊 Calcular datos del lote actual
        const startIndex = batchIndex * BATCH_SIZE;
        const endIndex = Math.min(startIndex + BATCH_SIZE, studentsData.length);
        const batchData = studentsData.slice(startIndex, endIndex);
        const batchSize = batchData.length;
        
        // 📈 Actualizar progreso antes de procesar el lote
        const preProcessPercentage = (totalProcessed / studentsData.length) * 100;
        
        setProgress(prev => ({
          ...prev,
          currentBatch: batchIndex + 1,
          processedStudents: totalProcessed,
          percentage: preProcessPercentage,
          currentStatus: `Procesando lote ${batchIndex + 1}/${totalBatches} (${batchSize} estudiantes)...`,
          estimatedTimeRemaining: calculateEstimatedTime(
            totalProcessed, 
            studentsData.length, 
            batchTimes, 
            startTimeMs
          )
        }));

        // 🌐 Enviar lote al servidor usando utilidad segura
        let result = await safeSaveFetch(`${API_BASE_URL}/api/save-progress-batch`, {
          studentsData: batchData,
          batchInfo: {
            currentBatch: batchIndex + 1,
            totalBatches: totalBatches,
            batchSize: batchSize
          }
        }, localStorage.getItem('token') || undefined);
        
        if (!result.success) {
          // 🛡️ ENHANCED CONFLICT DETECTION with retry logic
          const hasVersionConflicts = result.errors?.some((errorObj: any) => {
            const errorMessage = typeof errorObj === 'string' ? errorObj : (errorObj?.error || '');
            return errorMessage.includes('modificado por otro usuario') || 
                   errorMessage.includes('version conflict') ||
                   errorMessage.includes('Conflict:');
          }) || result.message?.includes('conflict');
          
          const isRetryableError = hasVersionConflicts || 
                                 result.message?.includes('timeout') || 
                                 result.message?.includes('network');
          
          // 🔄 RETRY LOGIC: Intentar hasta 2 veces por lote
          if (isRetryableError && (result.retryCount || 0) < 2) {
            console.warn(`⚠️ Retrying batch ${batchIndex + 1}/${totalBatches}, attempt ${(result.retryCount || 0) + 1}`);
            
            setProgress(prev => ({
              ...prev,
              currentStatus: `Reintentando lote ${batchIndex + 1}/${totalBatches} (intento ${(result.retryCount || 0) + 2}/3)...`,
              conflictsDetected: hasVersionConflicts ? (prev.conflictsDetected || 0) + 1 : prev.conflictsDetected
            }));
            
            // Esperar un poco antes de reintentar
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Reintentar el mismo lote usando utilidad segura
            const retryResult = await safeSaveFetch(`${API_BASE_URL}/api/save-progress-batch`, {
              studentsData: batchData,
              batchInfo: {
                currentBatch: batchIndex + 1,
                totalBatches: totalBatches,
                batchSize: batchSize,
                retryCount: (result.retryCount || 0) + 1
              }
            }, localStorage.getItem('token') || undefined);
            
            if (!retryResult.success) {
              // Si el reintento también falla, seguir con el error original
              if (hasVersionConflicts) {
                throw new Error(
                  `⚠️ CONFLICTO PERSISTENTE en lote ${batchIndex + 1}/${totalBatches}: Otro usuario modificó los mismos datos. ` +
                  (isConnected && isAuthenticated ? 
                    'El sistema de coordinación detectó el conflicto después de reintentos.' :
                    'Sin coordinación WebSocket. Por favor recarga la página.')
                );
              } else {
                throw new Error(`Error persistente en lote ${batchIndex + 1}/${totalBatches}: ${retryResult.message || 'Error desconocido'}`);
              }
            } else {
              // El reintento fue exitoso, usar el resultado del reintento
              result = retryResult;
              console.log(`✅ Batch ${batchIndex + 1}/${totalBatches} succeeded on retry`);
              setProgress(prev => ({
                ...prev,
                currentStatus: `Lote ${batchIndex + 1}/${totalBatches} recuperado exitosamente`,
                conflictsResolved: (prev.conflictsResolved || 0) + 1
              }));
            }
          } else {
            // No es un error recuperable o ya se intentó demasiadas veces
            if (hasVersionConflicts) {
              setProgress(prev => ({
                ...prev,
                conflictsDetected: (prev.conflictsDetected || 0) + 1
              }));
              
              throw new Error(
                `⚠️ CONFLICTO IRRECUPERABLE en lote ${batchIndex + 1}/${totalBatches}: Otro usuario modificó los mismos datos. ` +
                (isConnected && isAuthenticated ? 
                  'El sistema de coordinación no pudo resolver el conflicto después de múltiples intentos.' :
                  'Sin coordinación WebSocket. Por favor recarga la página.')
              );
            } else {
              throw new Error(`Error irrecuperable en lote ${batchIndex + 1}/${totalBatches}: ${result.message || 'Error desconocido'}`);
            }
          }
        }

        // ✅ Lote procesado exitosamente
        const batchEndTime = Date.now();
        const batchDuration = batchEndTime - batchStartTime;
        batchTimes.push(batchDuration);
        
        totalProcessed += batchSize;
        
        // 📈 Actualizar progreso después de procesar el lote
        const newPercentage = (totalProcessed / studentsData.length) * 100;
        
        setProgress(prev => ({
          ...prev,
          processedStudents: totalProcessed,
          percentage: newPercentage,
          currentStatus: `Lote ${batchIndex + 1}/${totalBatches} completado (${batchSize} estudiantes guardados)`,
          estimatedTimeRemaining: calculateEstimatedTime(
            totalProcessed, 
            studentsData.length, 
            batchTimes, 
            startTimeMs
          ),
          conflictsResolved: (prev.conflictsResolved || 0) + (result.conflictResolution?.resolved || 0)
        }));

        // 🎯 Pequeña pausa para que el usuario pueda ver el progreso
        if (batchIndex < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      // 🎉 Todos los lotes procesados exitosamente
      setProgress(prev => ({
        ...prev,
        currentBatch: totalBatches,
        processedStudents: studentsData.length,
        percentage: 100,
        currentStatus: '¡Guardado completado exitosamente!',
        estimatedTimeRemaining: 0
      }));
      
      // 🎉 Notificación de éxito
      addNotification({
        type: 'success',
        title: 'Guardado Completado',
        message: `${studentsData.length} estudiantes guardados en ${totalBatches} lotes`
      });
      
      setIsComplete(true);
      
      // 🚨 FIX: Resetear bandera de guardado al completar exitosamente
      isSavingRef.current = false;
      
      setTimeout(() => onComplete({ 
        success: true, 
        totalProcessed: studentsData.length,
        batchesProcessed: totalBatches,
        averageBatchTime: batchTimes.reduce((a, b) => a + b, 0) / batchTimes.length
      }), 1500);
      
    } catch (error: any) {
      console.error('Error saving progress:', error);
      setHasError(true);
      setProgress(prev => ({
        ...prev,
        currentStatus: `Error: ${error.message}`
      }));

      // 🚨 FIX: Resetear bandera de guardado al fallar
      isSavingRef.current = false;

      // Usar mensaje de error más amigable
      const friendlyError = createUserFriendlyError(error);
      onError(friendlyError);
    }
  };

  // 🧮 Función para calcular tiempo estimado basado en velocidad actual
  const calculateEstimatedTime = (
    processed: number,
    total: number,
    batchTimes: number[],
    startTime: number
  ): number => {
    if (processed === 0 || batchTimes.length === 0) {
      return 0;
    }

    // Calcular velocidad promedio de los últimos 3 lotes (o todos si son menos)
    const recentBatches = batchTimes.slice(-3);
    const averageBatchTime = recentBatches.reduce((sum, time) => sum + time, 0) / recentBatches.length;
    
    // Estimar lotes restantes
    const BATCH_SIZE = 5; // Procesar 5 estudiantes por lote para mejor estabilidad
    const remainingStudents = total - processed;
    const remainingBatches = Math.ceil(remainingStudents / BATCH_SIZE);
    
    // Calcular tiempo estimado en segundos
    const estimatedMs = remainingBatches * averageBatchTime;
    return Math.max(0, estimatedMs / 1000);
  };

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  if (!isVisible) return null;

  return (
    <div className="save-progress-overlay">
      <div className="save-progress-modal">
        {/* Header */}
        <div className="progress-header">
          <div className="progress-icon">
            {isComplete ? (
              <div className="success-icon">✅</div>
            ) : hasError ? (
              <div className="error-icon">❌</div>
            ) : (
              <div className="loading-spinner"></div>
            )}
          </div>
          <h2 className="progress-title">
            {isComplete ? '¡Guardado Completado!' : 
             hasError ? 'Error al Guardar' : 
             'Guardando Progreso'}
          </h2>
        </div>

        {/* Progress Information */}
        <div className="progress-info">
          <div className="status-text">{progress.currentStatus}</div>
          
          {/* Progress Bar */}
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill"
              style={{ width: `${progress.percentage}%` }}
            >
              <div className="progress-shine"></div>
            </div>
            <div className="progress-percentage">
              {Math.round(progress.percentage)}%
            </div>
          </div>

          {/* Coordination Status */}
          <div className="coordination-status">
            <div className="info-row">
              <span className="info-label">
                <FaWifi />
                Coordinación:
              </span>
              <span className="info-value coordination-active">
                Activa (HTTP)
              </span>
            </div>
            
            {(progress.conflictsDetected > 0 || progress.conflictsResolved > 0) && (
              <div className="info-row">
                <span className="info-label">
                  <FaSync />
                  Conflictos:
                </span>
                <span className="info-value conflict-stats">
                  {progress.conflictsResolved > 0 && (
                    <span className="conflicts-resolved">✅ {progress.conflictsResolved} resueltos</span>
                  )}
                  {progress.conflictsDetected > progress.conflictsResolved && (
                    <span className="conflicts-detected">⚠️ {progress.conflictsDetected - progress.conflictsResolved} pendientes</span>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Batch Information */}
          <div className="batch-info">
            <div className="info-row">
              <span className="info-label">
                <FaUsers />
                Estudiantes:
              </span>
              <span className="info-value">
                {progress.processedStudents} / {progress.totalStudents}
              </span>
            </div>
            
            <div className="info-row">
              <span className="info-label">
                📦 Lote actual:
              </span>
              <span className="info-value">
                {progress.currentBatch} / {progress.totalBatches}
              </span>
            </div>
            
            {progress.estimatedTimeRemaining > 0 && !isComplete && (
              <div className="info-row">
                <span className="info-label">
                  ⏱️ Tiempo estimado:
                </span>
                <span className="info-value estimated-time">
                  {formatTime(progress.estimatedTimeRemaining)}
                </span>
              </div>
            )}
            
            {progress.currentBatch > 1 && !isComplete && (
              <div className="info-row">
                <span className="info-label">
                  🚀 Velocidad:
                </span>
                <span className="info-value processing-speed">
                  {Math.round((progress.processedStudents / ((Date.now() - startTime) / 1000)) * 60)} est/min
                </span>
              </div>
            )}
          </div>

          {/* Processing Animation */}
          {!isComplete && !hasError && (
            <div className="processing-animation">
              <div className="dot dot1"></div>
              <div className="dot dot2"></div>
              <div className="dot dot3"></div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="progress-footer">
          {isComplete ? (
            <button 
              className="btn-close"
              onClick={() => onComplete({})}
            >
              Cerrar
            </button>
          ) : hasError ? (
            <button 
              className="btn-retry"
              onClick={startSaving}
            >
              Reintentar
            </button>
          ) : (
            <div className="saving-message">
              💾 Procesando datos en lotes para optimizar rendimiento...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveProgressModal;
